import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useHistoryStore } from "../stores/historyStore";
import { tokens } from "../styles/tokens";

/**
 * Asset travel paths as lit tubes with arrowheads running along them.
 *
 * The previous version drew 1.5 px drei `Line`s at 55% opacity. A screen-space
 * line does not thicken as you zoom in, so on a floor the size of wh-webinar
 * (~300 m across) eight tracks read as hairlines on navy, and nothing in a
 * static line says which way the truck was going -- the one thing a spaghetti
 * diagram exists to show.
 *
 * So: a tube with real width in metres, and arrowheads that travel it at a
 * constant ground speed. Motion carries the direction; the operator reads it
 * without a legend, from any zoom, at a glance.
 */

// One distinct color per asset slot (cycles if > 8 assets).
const PALETTE = [
  tokens.blue[300]   ?? "#93c5fd",
  "#4ade80",
  "#f472b6",
  "#fb923c",
  "#a78bfa",
  "#34d399",
  "#f87171",
  "#fbbf24",
];

/** Metres. Half a pallet wide: visible across a 300 m floor, not a pipeline. */
const TUBE_RADIUS_M = 0.22;
/** The tube rides above the heatmap (0.02) and the old trail height (0.05). */
const TRACK_Y = 0.18;
/** One arrowhead per this much path, so density reads as distance travelled. */
const ARROW_EVERY_M = 6;
/** Arrowheads travel at a fixed ground speed — deliberately not the asset's own
 *  speed, which would make a parked truck's track look dead rather than idle. */
const ARROW_SPEED_MPS = 5;
const ARROW_LEN_M = 1.1;
const ARROW_RADIUS_M = 0.42;
/** Samples closer than this are GPS jitter; feeding them to the spline gives
 *  near-zero tangents and the arrowheads spin on the spot. */
const MIN_STEP_M = 0.2;
/** Above this an arrow is a speck at any sane zoom; below it a short hop still
 *  gets one, so a two-point track is not silently direction-less. */
const MAX_ARROWS = 120;
/** Curve resolution. Tube segments beyond this buy nothing at warehouse scale. */
const MAX_TUBE_SEGMENTS = 600;

const UP = new THREE.Vector3(0, 1, 0);

interface Track {
  id: string;
  color: string;
  curve: THREE.CatmullRomCurve3;
  lengthM: number;
}

/** One asset's tube plus its moving arrowheads. */
function TrackMesh({ track }: { track: Track }) {
  const { curve, color, lengthM } = track;

  const geometry = useMemo(() => {
    const segments = Math.min(MAX_TUBE_SEGMENTS, Math.max(8, Math.round(lengthM * 2)));
    return new THREE.TubeGeometry(curve, segments, TUBE_RADIUS_M, 8, false);
  }, [curve, lengthM]);

  // TubeGeometry allocates buffers; React only drops the reference.
  useEffect(() => () => geometry.dispose(), [geometry]);

  const arrowCount = Math.min(MAX_ARROWS, Math.max(1, Math.floor(lengthM / ARROW_EVERY_M)));
  const meshRef = useRef<THREE.InstancedMesh>(null);
  // Scratch objects, reused every frame — allocating per arrow per frame is how
  // a 60 fps layer turns into a GC saw.
  const scratch = useMemo(() => ({
    m: new THREE.Matrix4(),
    q: new THREE.Quaternion(),
    p: new THREE.Vector3(),
    t: new THREE.Vector3(),
    s: new THREE.Vector3(1, 1, 1),
  }), []);

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh || lengthM <= 0) return;
    // Phase in METRES so every track scrolls at the same ground speed, whatever
    // its length; a u-space phase would race short tracks and crawl long ones.
    const travelled = (clock.getElapsedTime() * ARROW_SPEED_MPS) % lengthM;
    for (let i = 0; i < arrowCount; i++) {
      const along = (travelled + (i * lengthM) / arrowCount) % lengthM;
      const u = along / lengthM;
      curve.getPointAt(u, scratch.p);
      curve.getTangentAt(u, scratch.t).normalize();
      // The cone's axis is +Y; aim it down the direction of travel.
      scratch.q.setFromUnitVectors(UP, scratch.t);
      scratch.m.compose(scratch.p, scratch.q, scratch.s);
      mesh.setMatrixAt(i, scratch.m);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.55}
          roughness={0.55}
          metalness={0}
          transparent
          opacity={0.5}
          depthWrite={false}
        />
      </mesh>
      <instancedMesh
        ref={meshRef}
        args={[undefined, undefined, arrowCount]}
        frustumCulled={false}
      >
        {/* Radial segments low on purpose: at this size the silhouette is all
            the eye reads, and there may be hundreds on screen. */}
        <coneGeometry args={[ARROW_RADIUS_M, ARROW_LEN_M, 6]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.1}
          roughness={0.35}
          metalness={0}
          toneMapped={false}
        />
      </instancedMesh>
    </group>
  );
}

export function SpaghettiLayer() {
  const accumPts = useHistoryStore((s) => s.positionAccumulator);

  const tracks = useMemo<Track[]>(() => {
    const byAsset = new Map<string, { x: number; y: number; ts: number }[]>();
    for (const p of accumPts) {
      const arr = byAsset.get(p.assetId) ?? [];
      arr.push({ x: p.x, y: p.y, ts: p.ts });
      byAsset.set(p.assetId, arr);
    }

    const result: Track[] = [];
    let idx = 0;
    for (const [id, pts] of byAsset.entries()) {
      if (pts.length < 2) continue;
      pts.sort((a, b) => a.ts - b.ts);

      // Drop jitter before splining: a parked truck emits a cloud of samples
      // within centimetres of each other, and CatmullRom through that cloud
      // loops the tube into knots.
      const path: THREE.Vector3[] = [];
      for (const p of pts) {
        const v = new THREE.Vector3(p.x, TRACK_Y, p.y);
        const last = path[path.length - 1];
        if (last && last.distanceTo(v) < MIN_STEP_M) continue;
        path.push(v);
      }
      if (path.length < 2) continue;

      const curve = new THREE.CatmullRomCurve3(path, false, "catmullrom", 0.5);
      const lengthM = curve.getLength();
      if (lengthM <= 0) continue;

      result.push({ id, color: PALETTE[idx % PALETTE.length], curve, lengthM });
      idx++;
    }
    return result;
  }, [accumPts]);

  if (tracks.length === 0) return null;

  return (
    <>
      {tracks.map((track) => (
        <TrackMesh key={track.id} track={track} />
      ))}
    </>
  );
}
